/**
 * TimeCardModal — staff weekly time-card tool (Round 15).
 *
 * Faithful 1-to-1 React port of the old Softr `examples/time_card.html`. The original imperative
 * render/event logic is preserved verbatim inside `runTimeCard()`; only the data seams were swapped:
 *   - hardcoded Airtable PAT + direct api.airtable.com calls  → services/timecards.js (/api/airtable)
 *   - window.logged_in_user.airtable_record_id                → ctx.currentMember.id (Clerk)
 *   - PROXY_INTAKE fetch with x-aj-token                       → tc.submitIntake (Clerk bearer)
 *   - document.getElementById('aj-time-calendar')             → the modal's container ref
 * Document-level listeners + the poll interval are tracked and torn down on close. The original CSS
 * (scoped to #aj-time-calendar) is imported verbatim from timecard.css.
 *
 * Opens on the `open-timecard` CustomEvent. Available to all staff.
 */
import { useEffect, useRef, useState } from 'react'
import './timecard.css'
import { ctx } from '../outline/ctx.js'
import * as tc from '../services/timecards.js'
import { getCanSelfApprove } from '../services/timeGate.js'
import { getLockedDays } from '../services/timeGate.js'
import { skillsForTimeEntry } from '../outline/eligibility.js'
import { isSeatLoaded, isManagerOrAbove } from '../services/tenancy.js'
import { getActiveProjects } from '../services/commands.js'
import { openPhaseLandscape } from './phaseLandscape.js'
import TimeCardSuggestions from './TimeCardSuggestions.jsx'
import TimeCardExpenses from './TimeCardExpenses.jsx'
import { alertDialog, promptDialog } from './Dialog.jsx'

export default function TimeCardModal() {
  const [open, setOpen] = useState(false)
  const rootRef = useRef(null)
  const targetDateRef = useRef(null)   // deep-link target day (from open-timecard detail.date)

  useEffect(() => {
    function onOpen(e) { targetDateRef.current = (e && e.detail && e.detail.date) || null; setOpen(true) }
    window.addEventListener('open-timecard', onOpen)
    return () => window.removeEventListener('open-timecard', onOpen)
  }, [])

  useEffect(() => {
    if (!open) return
    function onKey(e) { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    let cleanup = () => {}
    try { cleanup = runTimeCard(rootRef.current, ctx.currentMember ? ctx.currentMember.id : null, targetDateRef.current) } catch (e) { /* eslint-disable-line */ console.error(e) }
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; try { cleanup() } catch (e) {} }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  if (!open) return null
  return (
    <div className="tc-overlay">
      <div className="tc-modal" data-modal-id="TimeCardModal">
        <div className="tc-modal-bar">
          <span className="tc-modal-title">Time Card</span>
          {isSeatLoaded() && isManagerOrAbove() && (
            <button type="button" onClick={() => window.dispatchEvent(new CustomEvent('timecard-manage-phases'))}
              style={{ marginLeft: 'auto', marginRight: 12, fontFamily: '"DM Sans", sans-serif', fontSize: 12, fontWeight: 600, color: '#07272D', background: '#fff', border: '0.5px solid #c9d3dc', borderRadius: 4, padding: '4px 10px', cursor: 'pointer' }}>
              Manage phases
            </button>
          )}
          <button className="tc-modal-close" title="Close (Esc)" onClick={() => setOpen(false)}>&times;</button>
        </div>
        <div className="tc-modal-scroll">
          <TimeCardSuggestions initialDate={targetDateRef.current} />
          <div id="aj-time-calendar" ref={rootRef}></div>
          <TimeCardExpenses initialDate={targetDateRef.current} />
        </div>
      </div>
    </div>
  )
}

// ── Ported tool ───────────────────────────────────────────────────────────────
// Verbatim logic from examples/time_card.html, with the data seams routed through
// services/timecards.js. Returns a cleanup function that removes all listeners.
function runTimeCard(container, submitterId, initialDate) {
  if (!container) return () => {}

  var ACTIVITY_CODES = [];

  // ── Data-seam shims (only these touch the service; the rest of the tool is unchanged) ──
  function atPatch(recordId, fields) { return tc.patchEntry(recordId, fields); }
  function atDelete(recordId) { return tc.deleteEntry(recordId); }

  function todayStr() {
    var d = new Date();
    return d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0');
  }

  // ── State ──
  var allRecords = [], selectedDate = null, weekOffset = 0;
  var announcedDay = null;   // last day announced to the Suggested section
  var editingRecordId = null, confirmingDeleteId = null, confirmingSubmitId = null;
  var intakePanelOpen = false, intakeSubmitting = false;
  // Manager/lead-only escape hatch: a phase missing from the time-entry picker (Show in Time Entry
  // Submission) can't be turned back on from anywhere reachable during Time Gate lockout — the phase
  // landscape modal was previously only reachable from Time Review, which isn't shown to someone
  // gated on their OWN hours (only reviewer-backlog gating opens it). Surfacing it here too means a
  // manager stuck in personal lockout can fix it without needing to Snooze past the gate first.
  var managePhasesOpen = false, managePhasesQuery = '', managePhasesProjects = null;
  var parsingState = null, parsingRecordCount = 0, parsingStartTime = 0;
  var searchQuery = '', monthViewOpen = false, monthOffset = 0, allPhases = [];
  var myWorkItems = [], myWorkItemsById = {};   // submitter's work list for entity-attribution edits
  var projMilestones = {};   // projectUid -> [{ id, name, phase }] | 'loading' (the milestone picker)
  var ENT_ABBR = { 'Drawing Sheet': 'Sheet', 'BIM System': 'BIM', 'Coordination Item': 'Coord', 'Thread': 'Thread', 'Deliverable': 'Milestone', 'Untied': 'Not tied', 'Agency Review': 'Agency', 'Opportunity': 'Opp', 'Lead': 'Lead', 'Billing Run': 'Billing' };
  var fetchInFlight = false, lastStatusChange = 0, initialized = false;
  var targetEBR = 125;
  var selectedBulkIds = {}, bulkDeleteConfirming = false, bulkSubmitConfirming = false;
  var unsubmittedView = false;
  var rejectedOnly = false;   // Rejected sub-view — a filtered Unsubmitted view (shares its card/bulk logic)
  // Round 16.4 — if this person reviews their own time (holds the review module + nobody else is
  // assigned to review them), submitting auto-approves + posts to BQE Core (no pointless self-review).
  var canSelfApprove = false;
  var lastFetchTime = null, lastFetchError = null, fetchingNow = false;
  var STATUS_CHANGE_GRACE = 10000;

  // ── Enforcement-gate lock (Round 16): days cleared through the gate are read-only here ──
  var lockedDates = {};
  function isLocked(ds) { return !!lockedDates[ds]; }
  function recordLocked(rid) { var r = allRecords.find(function(x){return x.id===rid;}); return r ? isLocked(getEntryDate(r)) : false; }
  function refreshLocked() {
    var sid = getSubmitterId(); if (!sid) return;
    getLockedDays(sid).then(function(ds){ lockedDates = {}; (ds||[]).forEach(function(d){ lockedDates[String(d).slice(0,10)] = true; }); if (initialized && !editingRecordId) render(); }).catch(function(){});
  }
  // Tell the enforcement overlay to re-evaluate after the user submits/changes time.
  function fireGateRecheck() { try { setTimeout(function(){ window.dispatchEvent(new CustomEvent('time-gate-recheck')); refreshLocked(); }, 700); } catch (e) {} }
  // Approve + post one entry to BQE Core in one go (self-reviewers only). Best-effort post: if BQE
  // fails the entry stays Approved and the normal post-retry path handles it.
  function selfApproveAndPost(id) {
    return tc.approveEntry(id).then(function(){ return tc.postTimeEntryToCore(id).catch(function(){}); });
  }

  // ── Fetch ──
  function getSubmitterId() { return submitterId || null; }

  function fetchRecords(callback) {
    if (fetchInFlight) return;
    var sid = getSubmitterId();
    if (!sid) {
      container.innerHTML = '<div class="loading" style="color:var(--red)">Could not identify the logged-in user. Please refresh.</div>';
      return;
    }
    doFetch(sid, callback);
  }

  function doFetch(sid, callback) {
    fetchInFlight = true; fetchingNow = true;
    tc.getMyTimeRecords(sid)
      .then(function(records) {
        fetchInFlight = false; fetchingNow = false;
        lastFetchTime = new Date(); lastFetchError = null;
        callback(records || []);
      })
      .catch(function() {
        fetchInFlight = false; fetchingNow = false;
        lastFetchError = 'Network error — check connection';
        callback([]);
      });
  }

  function refreshRecords() {
    if (fetchInFlight) return;
    fetchingNow = true; render();
    fetchRecords(function(records) {
      if (parsingState === 'parsing' && records.length > parsingRecordCount && (Date.now() - parsingStartTime) > 15000) {
        parsingState = null;
      }
      allRecords = records;
      if (!editingRecordId) render();
    });
  }

  function fetchActivities() {
    tc.getActivities().then(function(list) { ACTIVITY_CODES = list || []; }).catch(function() {});
  }
  function fetchMyWorkItems() {
    var sid = getSubmitterId(); if (!sid) return;
    tc.getSubmitterWorkItems(sid).then(function(list) {
      // Keep RAW items ({entity_type, entity_id, entity_name, project_name}) — scopeWorkItemsForEntry
      // needs project_name + entity_type. The display string is built after scoping (entityItemsFor).
      myWorkItems = list || [];
      myWorkItemsById = {};
      myWorkItems.forEach(function(w) { myWorkItemsById[w.entity_id] = { type: w.entity_type, ename: w.entity_name }; });
    }).catch(function() {});
  }
  function fetchPhases() {
    tc.getEntryPhases().then(function(list) { allPhases = list || []; }).catch(function() {});
  }
  function fetchSettings() {
    return tc.getTargetEbr().then(function(v) { if (isFinite(v) && v > 0) targetEBR = v; }).catch(function() {});
  }

  // ── Utilities ──
  function getMonday(d) {
    var dt = new Date(d), day = dt.getDay();
    dt.setDate(dt.getDate() - day + (day === 0 ? -6 : 1)); dt.setHours(0,0,0,0); return dt;
  }
  function addDays(date, n) { var d = new Date(date); d.setDate(d.getDate() + n); return d; }
  function formatDate(date) { return date.getFullYear() + '-' + String(date.getMonth()+1).padStart(2,'0') + '-' + String(date.getDate()).padStart(2,'0'); }
  function formatDisplay(date) { return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }); }
  function formatDateConfirm(ds) {
    if (!ds || !/^\d{4}-\d{2}-\d{2}$/.test(ds)) return '';
    var d = new Date(ds + 'T00:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  }
  function formatWeekRange(s, e) { return s.toLocaleDateString('en-US',{month:'short',day:'numeric'}) + ' – ' + e.toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}); }
  var DAY_NAMES = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

  function getWeekDays() {
    var base = getMonday(new Date()); base.setDate(base.getDate() + weekOffset * 7);
    return Array.from({length:7}, function(_,i) { return addDays(base, i); });
  }
  function getEntryDate(r) { return (r.fields['Entry Date'] || '').split('T')[0].slice(0,10); }
  function hoursForDate(ds) { return allRecords.filter(function(r){return getEntryDate(r)===ds;}).reduce(function(s,r){return s+(parseFloat(r.fields['Actual Hours'])||0);},0); }
  function billableHoursForDate(ds) {
    return allRecords.filter(function(r) {
      if (getEntryDate(r)!==ds) return false;
      return r.fields['Billable']===true;
    }).reduce(function(s,r){return s+(parseFloat(r.fields['Actual Hours'])||0);},0);
  }
  function unsubmittedHoursForDate(ds) {
    return allRecords.filter(function(r) {
      if (getEntryDate(r)!==ds) return false; var st=getStatusLabel(r); return st==='Pending Review'||st==='Rejected';
    }).reduce(function(s,r){return s+(parseFloat(r.fields['Actual Hours'])||0);},0);
  }
  function entriesForDate(ds) { return allRecords.filter(function(r){return getEntryDate(r)===ds;}); }
  function weekTotal() { return getWeekDays().reduce(function(s,d){return s+hoursForDate(formatDate(d));},0); }
  function weekUnsubmittedTotal() { return getWeekDays().reduce(function(s,d){return s+unsubmittedHoursForDate(formatDate(d));},0); }
  function weekBillableTotal() { return getWeekDays().reduce(function(s,d){return s+billableHoursForDate(formatDate(d));},0); }
  function formatHours(h) { return h.toFixed(2) + ' hrs'; }
  function getStatusLabel(r) { var s=r.fields['Status']; if(!s) return 'Pending Review'; return (typeof s==='object'&&s.label)?s.label:s; }
  function statusPillClass(label) {
    return {'Pending Review':'status-pending-review','Submitted':'status-submitted','Approved':'status-approved',
      'Rejected':'status-rejected','Posted':'status-posted','Post Failed':'status-post-failed'}[label]||'status-pending-review';
  }
  function rowClass(label) { return label==='Rejected'?' rejected-row':label==='Post Failed'?' failed-row':''; }

  // ── Reload guards ──
  function canReload() {
    return !fetchInFlight && !editingRecordId && !confirmingDeleteId && !confirmingSubmitId && !intakePanelOpen
      && !bulkSubmitConfirming
      && (Date.now() - lastStatusChange > STATUS_CHANGE_GRACE);
  }
  function scheduleReload(ms) { setTimeout(function(){ if(canReload()) refreshRecords(); }, ms); }

  // ── Local state helpers ──
  function updateLocalStatus(rid, s) {
    var r = allRecords.find(function(x){return x.id===rid;}); if(r) r.fields['Status'] = {label:s};
  }
  function updateLocalFields(rid, fields) {
    var r = allRecords.find(function(x){return x.id===rid;}); if(!r) return;
    Object.keys(fields).forEach(function(k){
      if(k==='Status') r.fields['Status'] = {label:fields[k]};
      else if(k==='Activity Code') { r.fields['Activity Code']={id:fields[k]}; var m=ACTIVITY_CODES.find(function(a){return a.id===fields[k];}); if(m) r.fields['Activity Code String']=m.label; }
      else if(k==='Phase Airtable ID') r.fields['Phase Airtable ID'] = fields[k] ? [fields[k]] : [];
      else r.fields[k] = fields[k];
    });
  }

  // ── Render ──
  function render() {
    var days=getWeekDays(), todayS=formatDate(new Date()), selectedStr=selectedDate||todayS;
    var weekStrs=days.map(function(d){return formatDate(d);});
    if(weekStrs.indexOf(selectedStr)===-1){selectedStr=weekStrs[0];selectedDate=selectedStr;}
    var entries=entriesForDate(selectedStr);
    var dayTotal=hoursForDate(selectedStr), dayUnsub=unsubmittedHoursForDate(selectedStr);
    var dayBil=billableHoursForDate(selectedStr), dayBilPct=dayTotal>0?Math.round(dayBil/dayTotal*100):null;
    var wTotal=weekTotal(), wUnsub=weekUnsubmittedTotal(), wBil=weekBillableTotal(), wBilPct=wTotal>0?Math.round(wBil/wTotal*100):null;
    var selDateObj=days[weekStrs.indexOf(selectedStr)];
    var html='';

    // Manage phases, opened from the Time Card's title bar (Matt, 2026-10-22: from the top of the whole
    // card, not only inside New Entry). Shown first so it is in view when it opens.
    if(managePhasesOpen){
      html+='<div class="mgphase-panel" id="mgphase-panel"><div class="mgphase-hint" style="display:flex;justify-content:space-between;gap:8px">Find a project to turn time entry on or off for its phases.'
        +'<button class="intake-manage-phases-btn" id="mgphase-toggle-btn" type="button">Close</button></div>';
      html+='<div class="sd-wrap" data-sd="mgphase-project" style="position:relative">';
      html+='<input class="sd-input" id="mgphase-input" type="text" placeholder="Search projects..." autocomplete="off" value="'+managePhasesQuery.replace(/"/g,'&quot;')+'">';
      html+='</div></div>';
    }

    if(lastFetchError){
      html+='<div class="fetch-bar error">⚠️ '+lastFetchError+'</div>';
    } else if(lastFetchTime){
      html+='<div class="fetch-bar">'+(fetchingNow?'<span class="fetch-spinner"></span>':'')
        +allRecords.length+' records · updated '+lastFetchTime.toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit'})+'</div>';
    } else {
      html+='<div class="fetch-bar"><span class="fetch-spinner"></span> Loading…</div>';
    }

    // Entries that never reached BQE Core, on ANY day (Matt, 2026-10-19: "it doesn't show me I had a
    // failed post anywhere"). Before this the only sign was the pill on that day's own row, so a post
    // that failed on a past day went unseen. Each line opens its day.
    var failed=allRecords.filter(function(r){return getStatusLabel(r)==='Post Failed';})
      .sort(function(a,b){return getEntryDate(a)<getEntryDate(b)?1:-1;});
    if(failed.length){
      var escH=function(s){return String(s||'').replace(/[&<>"]/g,function(c){return {'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;'}[c];});};
      var why=function(pe){
        pe=String(pe||'');
        if(/ProjectControlLimitation|Project Assignment does not allow/i.test(pe)) return 'Core won’t take time from you on this project (its assignment settings)';
        if(/rate/i.test(pe)) return 'Core is missing a bill rate for this';
        return pe?'Core refused it':'It hasn’t reached Core';
      };
      var fh=failed.reduce(function(s,r){return s+(parseFloat(r.fields['Actual Hours'])||0);},0);
      html+='<div style="margin:0 0 10px;padding:10px 12px;border:0.5px solid #c0392b;border-radius:8px;background:rgba(192,57,43,0.06);font-size:12px">';
      html+='<div style="font-weight:600;color:#c0392b;margin-bottom:4px">'+failed.length+' '+(failed.length===1?'entry':'entries')+' ('+(Math.round(fh*10)/10)+' h) didn’t reach BQE Core</div>';
      html+='<div style="color:var(--text-soft);margin-bottom:6px">'+(canSelfApprove?'They don’t count toward your hours until they post. Open the day, fix the phase or project and press Retry post.':'Your reviewer has these to fix and re-post.')+'</div>';
      failed.forEach(function(r){
        var d=getEntryDate(r), dl=d?new Date(d+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric'}):'';
        html+='<div style="display:flex;gap:8px;align-items:baseline;padding:2px 0">'
          +'<button class="btn btn-outline" style="padding:1px 8px;font-size:11px" data-action="goto-day" data-date="'+d+'">'+dl+'</button>'
          +'<span style="font-family:\'DM Mono\',monospace">'+(parseFloat(r.fields['Actual Hours'])||0)+' h</span>'
          +'<span style="flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">'+escH(r.fields['Memo Draft'])+'</span>'
          +'<span style="color:#c0392b;white-space:nowrap">'+escH(why(r.fields['Post Error']))+'</span></div>';
      });
      html+='</div>';
    }

    html+='<div class="cal-header"><div class="cal-title">Time Entries</div><div class="cal-nav">';
    html+='<button class="cal-nav-btn" id="aj-this-week" style="font-size:11px;font-weight:600;width:auto;padding:0 8px;">Today</button>';
    html+='<button class="cal-nav-btn" id="aj-prev-week">&#8592;</button>';
    html+='<span class="cal-week-label">'+formatWeekRange(days[0],days[6])+'</span>';
    html+='<button class="cal-nav-btn" id="aj-next-week">&#8594;</button>';
    html+='<button class="cal-month-btn'+(monthViewOpen?' active':'')+'" id="aj-month-toggle">&#9776;</button>';
    html+='</div></div>';

    var unsubCount=allRecords.filter(function(r){return getStatusLabel(r)==='Pending Review';}).length;
    var rejCount=allRecords.filter(function(r){return getStatusLabel(r)==='Rejected';}).length;
    html+='<div class="view-toggle-wrap">';
    html+='<button class="view-toggle-btn'+(unsubmittedView?'':' active')+'" id="aj-view-week">Week</button>';
    html+='<button class="view-toggle-btn'+((unsubmittedView&&!rejectedOnly)?' active':'')+'" id="aj-view-unsub">Unsubmitted';
    if(unsubCount>0) html+=' <span class="unsub-badge">'+unsubCount+'</span>';
    html+='</button>';
    html+='<button class="view-toggle-btn'+(rejectedOnly?' active':'')+'" id="aj-view-rejected">Rejected';
    if(rejCount>0) html+=' <span class="unsub-badge" style="background:#C0392B">'+rejCount+'</span>';
    html+='</button>';
    html+='</div>';

    if(intakePanelOpen){
      html+='<div class="intake-panel"><div class="intake-title">New Time Entry</div>';
      html+='<div class="intake-fields">';
      html+='<div class="intake-field"><label>Date</label><input type="date" id="intake-date" value="'+selectedStr+'">';
      html+='<span class="intake-date-confirm" id="intake-date-confirm">'+formatDateConfirm(selectedStr)+'</span></div>';
      html+='<div class="intake-field"><label>Work Description</label><textarea id="intake-narrative" placeholder="Describe what you worked on today…"></textarea></div>';
      html+='</div><div class="intake-actions">';
      html+='<button class="btn btn-primary" id="intake-submit-btn"'+(intakeSubmitting?' disabled':'')+'>'+(intakeSubmitting?'Submitting<span class="aj-dots"></span>':'Submit')+'</button>';
      html+='<button class="btn btn-cancel" id="intake-cancel-btn">Cancel</button>';
      html+='<span class="feedback-msg" id="intake-feedback"></span></div></div>';
    }

    if(monthViewOpen){
      var now=new Date(), mvy=now.getFullYear(), mvm=now.getMonth()+monthOffset;
      while(mvm>11){mvm-=12;mvy++;} while(mvm<0){mvm+=12;mvy--;}
      var mvFirst=new Date(mvy,mvm,1), mvLast=new Date(mvy,mvm+1,0);
      var mvTitle=mvFirst.toLocaleDateString('en-US',{month:'long',year:'numeric'});
      var startDow=mvFirst.getDay();
      var DOW=['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
      html+='<div class="month-view"><div class="month-header"><span class="month-title">'+mvTitle+'</span>';
      html+='<div class="month-nav"><button class="cal-nav-btn" id="aj-month-prev">&#8592;</button><button class="cal-nav-btn" id="aj-month-next">&#8594;</button></div></div>';
      html+='<div class="month-grid">';
      DOW.forEach(function(d){html+='<div class="month-dow">'+d+'</div>';});
      for(var b=0;b<startDow;b++) html+='<div></div>';
      for(var dd=1;dd<=mvLast.getDate();dd++){
        var mvD=new Date(mvy,mvm,dd), mvDs=formatDate(mvD), mvDow=mvD.getDay();
        var mvH=hoursForDate(mvDs), mvTgt=(mvDow>=1&&mvDow<=4)?9:(mvDow===5?4:0);
        var mvDot=mvTgt>0?(mvH===0?'dot-empty':mvH>=mvTgt?'dot-met':'dot-partial'):'';
        var mvCls='month-day'+(mvDs===todayS?' month-today':'')+(mvDs===selectedStr?' month-selected':'');
        html+='<div class="'+mvCls+'" data-month-date="'+mvDs+'">';
        html+='<span class="month-day-num">'+dd+'</span>';
        if(mvH>0) html+='<span class="month-day-hrs">'+mvH.toFixed(1)+'</span>';
        if(mvDot) html+='<div class="month-day-dot '+mvDot+'"></div>';
        html+='</div>';
      }
      html+='</div></div>';
    }

    if(unsubmittedView){
      var unsubRecords=allRecords.filter(function(r){
        var s=getStatusLabel(r);
        return rejectedOnly ? (s==='Rejected') : (s==='Pending Review'||s==='Rejected');
      });
      unsubRecords.sort(function(a,b){
        var da=a.fields['Entry Date']||'', db=b.fields['Entry Date']||'';
        return da<db?-1:da>db?1:0;
      });

      if(unsubRecords.length===0){
        html+='<div class="unsub-empty">'+(rejectedOnly?'No rejected entries — nothing sent back.':'No unsubmitted entries — you\'re all caught up.')+'</div>';
      } else {
        var unsubByDate={}, unsubDateOrder=[];
        unsubRecords.forEach(function(r){
          var d=(r.fields['Entry Date']||'').split('T')[0];
          if(!unsubByDate[d]){unsubByDate[d]=[];unsubDateOrder.push(d);}
          unsubByDate[d].push(r);
        });

        unsubDateOrder.forEach(function(ds){
          var dateRecs=unsubByDate[ds];
          var dateObj=new Date(ds+'T12:00:00');
          var dateLabel=dateObj.toLocaleDateString('en-US',{weekday:'long',month:'long',day:'numeric',year:'numeric'});
          var dateTotal=dateRecs.reduce(function(s,r){return s+parseFloat(r.fields['Actual Hours']||0);},0);
          var eligIds=dateRecs.map(function(r){return r.id;});
          var allSel=eligIds.every(function(id){return selectedBulkIds[id];});

          html+='<div class="entries-panel" style="margin-bottom:16px;">';
          html+='<div class="entries-header">';
          html+='<span class="entries-date">';
          html+='<input type="checkbox" class="bulk-cb" data-action="bulk-select-all" data-ids="'+eligIds.join(',')+'"'+(allSel?' checked':'')+' >';
          html+=dateLabel+'</span>';
          html+='<span class="entries-total">'+formatHours(dateTotal)+'</span>';
          html+='</div>';

          dateRecs.forEach(function(record){
            html+=renderEntryCard(record, true);
          });
          html+='</div>';
        });

        var selCount=Object.keys(selectedBulkIds).filter(function(id){return selectedBulkIds[id];}).length;
        if(selCount>0){
          html+='<div class="bulk-bar">';
          if(bulkSubmitConfirming){
            html+='<span class="bulk-bar-label">Submit '+selCount+' entr'+(selCount===1?'y':'ies')+'?</span>';
            html+='<button class="bulk-bar-btn bulk-submit-btn" data-action="bulk-submit-confirm">Confirm Submit</button>';
            html+='<button class="bulk-bar-btn bulk-cancel-btn" data-action="bulk-submit-cancel">Cancel</button>';
          } else {
            html+='<span class="bulk-bar-label">'+selCount+' selected</span>';
            html+='<button class="bulk-bar-btn bulk-submit-btn" data-action="bulk-submit-prompt">Submit Selected</button>';
          }
          html+='</div>';
        }
      }

      html+='<div class="search-bar" style="margin-top:16px;"><div style="flex:1;"></div>';
      html+='<button class="btn-new-entry" id="aj-new-entry-btn">&#43; New Entry</button></div>';

    } else {

    html+='<div class="week-grid">';
    days.forEach(function(day,i){
      var ds=formatDate(day), hrs=hoursForDate(ds), uHrs=unsubmittedHoursForDate(ds), bHrs=billableHoursForDate(ds);
      var bPct=hrs>0?Math.round(bHrs/hrs*100):null;
      var cls='day-cell'+(ds===selectedStr?' active':'')+(ds===todayS?' today':'')+(i>=5?' weekend':'');
      var tgt=i<4?9:(i===4?4:0), dot='', tip='';
      if(tgt>0){var rem=Math.round((tgt-hrs)*10)/10;if(hrs===0){dot='dot-empty';tip=tgt+' hrs needed';}else if(rem<=0){dot='dot-met';var over=Math.round((hrs-tgt)*10)/10;tip='Target met'+(over>0?' · '+over.toFixed(1)+' hrs over':'');}else{dot='dot-partial';tip=rem.toFixed(1)+' hrs to go';}}
      html+='<div class="'+cls+'" data-date="'+ds+'">';
      if(tip) html+='<div class="day-tooltip">'+tip+'</div>';
      html+='<span class="day-name">'+DAY_NAMES[i]+'</span><span class="day-num">'+day.getDate()+'</span>';
      if(hrs>0){
        var hd=hrs.toFixed(2)+' hrs';
        if(uHrs>0) hd+=' <span style="color:var(--amber);font-size:10px">('+uHrs.toFixed(2)+' unsub)</span>';
        html+='<span class="day-hours">'+hd+'</span>';
        if(bPct!==null){var pc=ds===selectedStr?'rgba(255,255,255,0.75)':'var(--text-soft)';html+='<span style="font-size:9px;color:'+pc+';font-family:\'DM Mono\',monospace">'+bPct+'% bil</span>';}
      } else { html+='<span class="day-hours zero">—</span>'; }
      if(dot) html+='<div class="day-dot '+dot+'"></div>';
      html+='</div>';
    });
    html+='</div>';

    if(parsingState==='parsing'){
      html+='<div class="parsing-banner"><div class="parsing-spinner"></div><div class="parsing-text">';
      html+='<span class="parsing-title">Parsing your narrative…</span>';
      html+='<span class="parsing-sub">AI is reading your entry and creating time records. Sit tight — this usually takes 15–30 seconds.</span>';
      html+='</div></div>';
    }

    html+='<div class="search-bar"><div class="search-input-wrap">';
    html+='<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>';
    html+='<input class="search-input" id="aj-search" type="text" placeholder="Search entries..." value="'+searchQuery.replace(/"/g,'&quot;')+'">';
    if(searchQuery) html+='<button class="search-clear" id="aj-search-clear">&times;</button>';
    html+='</div><button class="btn-new-entry" id="aj-new-entry-btn">&#43; New Entry</button></div>';

    } // end week view

    var fEntries=searchQuery?entries.filter(function(r){
      var q=searchQuery.toLowerCase();
      var p=(Array.isArray(r.fields['Project Name'])?r.fields['Project Name'][0]:(r.fields['Project Name']||'')).toLowerCase();
      var m=(r.fields['Memo Draft']||'').toLowerCase();
      return p.indexOf(q)!==-1||m.indexOf(q)!==-1;
    }):entries;

    if(!unsubmittedView){
      html+='<div class="entries-panel">';
      var eligibleEntries=entries.filter(function(r){var s=getStatusLabel(r);return s==='Pending Review'||s==='Rejected';});
      var eligibleIds=eligibleEntries.map(function(r){return r.id;});
      var allEligibleSelected=eligibleIds.length>0&&eligibleIds.every(function(id){return selectedBulkIds[id];});
      html+='<div class="entries-header">';
      html+='<span class="entries-date">';
      if(eligibleIds.length>0){
        html+='<input type="checkbox" class="bulk-cb" id="bulk-select-all"'+(allEligibleSelected?' checked':'')+' data-action="bulk-select-all" data-ids="'+eligibleIds.join(',')+'">';
      }
      html+=formatDisplay(selDateObj)+'</span>';
      html+='<span class="entries-total">'+formatHours(dayTotal);
      if(dayUnsub>0) html+=' <span style="color:var(--amber);font-size:11px">('+dayUnsub.toFixed(2)+' unsub)</span>';
      if(dayBilPct!==null) html+=' <span style="color:var(--text-soft);font-size:11px;font-family:\'DM Mono\',monospace">· '+dayBilPct+'% bil</span>';
      html+='</span></div>';

      if(fEntries.length===0){
        html+='<div class="no-entries">'+(searchQuery?'No entries match your search.':'No entries for this day.')+'</div>';
      } else {
        fEntries.forEach(function(record){ html+=renderEntryCard(record, false); });
      }
      html+='</div>';
    }

    // Footer
    html+='<div class="cal-footer"><span class="footer-label">Week Total</span><span class="footer-hours">'+formatHours(wTotal);
    if(wUnsub>0) html+=' <span style="color:var(--amber);font-size:12px;font-weight:500">('+wUnsub.toFixed(2)+' unsub)</span>';
    if(wBilPct!==null) html+=' <span style="color:var(--text-soft);font-size:12px;font-weight:500;font-family:\'DM Mono\',monospace">· '+wBilPct+'% bil</span>';
    html+='</span></div>';

    var bulkIds=Object.keys(selectedBulkIds).filter(function(k){return selectedBulkIds[k];});
    if(bulkIds.length>0 && !unsubmittedView){
      var bulkHrs=bulkIds.reduce(function(s,id){
        var r=allRecords.find(function(x){return x.id===id;}); return s+(r?parseFloat(r.fields['Actual Hours'])||0:0);
      },0);
      html+='<div class="bulk-bar" id="aj-bulk-bar">';
      html+='<span class="bulk-bar-count">'+bulkIds.length+' entr'+(bulkIds.length===1?'y':'ies')+' selected · '+bulkHrs.toFixed(2)+' hrs</span>';
      if(bulkDeleteConfirming){
        html+='<div class="bulk-confirm-row"><span>Delete all selected entries?</span>';
        html+='<button class="bulk-bar-btn bulk-delete-btn" data-action="bulk-delete-confirm">Confirm Delete</button>';
        html+='<button class="bulk-bar-btn bulk-cancel-btn" data-action="bulk-delete-cancel">Cancel</button></div>';
      } else if(bulkSubmitConfirming){
        html+='<div class="bulk-confirm-row"><span>By submitting you attest all selected entries are accurate.</span>';
        html+='<button class="bulk-bar-btn bulk-submit-btn" data-action="bulk-submit-confirm">Confirm Submit</button>';
        html+='<button class="bulk-bar-btn bulk-cancel-btn" data-action="bulk-submit-cancel">Cancel</button></div>';
      } else {
        html+='<button class="bulk-bar-btn bulk-submit-btn" data-action="bulk-submit-prompt">Submit All</button>';
        html+='<button class="bulk-bar-btn bulk-delete-btn" data-action="bulk-delete-prompt">Delete All</button>';
        html+='<button class="bulk-bar-btn bulk-cancel-btn" data-action="bulk-clear">Clear</button>';
      }
      html+='</div>';
    }

    container.innerHTML = html;
    bindEvents();
    // Tell the Suggested section (TimeCardSuggestions, React) which day is showing.
    // The day's projects go with it, so the Expenses section (TimeCardExpenses) can offer them first.
    // Each project's phase that day goes too: an expense defaults to the phase the time went to.
    var dayProjects=[], dayPhases={};
    entries.forEach(function(r){
      var raw=r.fields['Project Name']; var p=Array.isArray(raw)?raw[0]:raw;
      var ph=r.fields['Phase Airtable ID']; ph=Array.isArray(ph)?ph[0]:ph;
      if(p&&dayProjects.indexOf(p)===-1) dayProjects.push(p);
      if(p&&ph&&!dayPhases[p]) dayPhases[p]=ph;
    });
    var announceKey=selectedStr+'|'+dayProjects.join('|')+'|'+Object.keys(dayPhases).map(function(k){return dayPhases[k];}).join('|');
    if (announceKey !== announcedDay) {
      announcedDay = announceKey;
      window.dispatchEvent(new CustomEvent('timecard-day', { detail: { date: selectedStr, projectNames: dayProjects, phaseByProject: dayPhases } }));
    }
  }

  // Shared entry-card renderer (identical markup for week + unsubmitted views).
  function renderEntryCard(record, isUnsubView){
    var f=record.fields, rid=record.id;
    var project=Array.isArray(f['Project Name'])?f['Project Name'][0]:(f['Project Name']||'');
    var phase=Array.isArray(f['Phase Name'])?f['Phase Name'][0]:(f['Phase Name']||'');
    var memo=f['Memo Draft']||'', hours=parseFloat(f['Actual Hours'])||0;
    var actRaw=f['Activity Code String'];
    var actCode=(actRaw?(Array.isArray(actRaw)?String(actRaw[0]||''):String(actRaw)):'' ).trim();
    // Core refuses an activity code that doesn't fit the project (00-01 = OP-/PTO-, projects = AS-);
    // such an entry can't be submitted until the code is fixed (the server enforces this too).
    var codeWhy=tc.activityCodeProblem(phase, actCode);
    var codeWhyHtml=codeWhy?'<span class="action-note" style="color:var(--red)">&#9888; '+String(codeWhy).replace(/[<>&]/g,'')+' Edit the entry to submit.</span>':'';
    var billable=f['Billable']===true;
    var statusLabel=getStatusLabel(record), isEditing=editingRecordId===rid;
    var conf=f['Confidence']?(typeof f['Confidence']==='object'?(f['Confidence'].label||'').toLowerCase():String(f['Confidence']).toLowerCase()):'';
    var revNotes=f['Review Notes']||'';
    var locked=isLocked(getEntryDate(record));
    var isBulkEligible=(statusLabel==='Pending Review'||statusLabel==='Rejected')&&!locked;
    var isBulkSelected=!!selectedBulkIds[rid];
    var html='';
    html+='<div class="entry-row'+rowClass(statusLabel)+(isEditing?' editing':'')+(isBulkSelected?' bulk-selected':'')+'" data-record-id="'+rid+'">';
    if(isUnsubView){
      html+='<input type="checkbox" class="bulk-cb bulk-entry-cb" data-action="bulk-toggle" data-record-id="'+rid+'"'+(isBulkSelected?' checked':'')+' style="margin-top:3px;">';
    } else if(isBulkEligible){
      html+='<input type="checkbox" class="bulk-cb bulk-entry-cb" data-action="bulk-toggle" data-record-id="'+rid+'"'+(isBulkSelected?' checked':'')+' style="margin-top:3px;">';
    } else {
      html+='<input type="checkbox" class="bulk-cb" disabled style="opacity:0.4;margin-top:3px;cursor:default;">';
    }
    html+='<div class="entry-body"><div class="entry-top"><div class="entry-info">';
    html+='<div class="entry-project">'+(project||'<span style="color:var(--red)">⚠ No project assigned</span>')+'</div>';
    if(phase){
      var phData=allPhases.find(function(p){return p.name===phase;});
      var phHealthHtml='';
      if(phData&&project.indexOf('Alder James Overhead')===-1){
        // Mirrors phaseFinancials.js#computeLiveFinancials' isFixedFee: Contract Type is
        // inconsistently populated (admin/support-style phases — Permitting, Meetings,
        // Construction Support — routinely leave it blank rather than "Hourly"), so treat a phase
        // as Fixed-Fee only when it actually has a contracted amount to dilute against. Fixed
        // 2026-08-07 — blank type used to default to Fixed-Fee, showing "Over by X hrs" against a
        // phantom $0 budget (see Osborne Pismo Beach ADU's Permitting Phase).
        var isPhaseHourly=phData.contractType==='Hourly'||!(phData.contractedFee>0);
        if(isPhaseHourly){
          var ebr=phData.effectiveBillingRate;
          if(ebr!=null&&isFinite(ebr)&&ebr>0&&targetEBR>0){
            var ebrRatio=ebr/targetEBR;
            var hClass2,hLabel2,tipText2;
            if(ebrRatio>1.7){hClass2='health-over';hLabel2='⚠ Too much senior time';}
            else if(ebrRatio>1.4){hClass2='health-approaching';hLabel2='Too much senior time?';}
            else if(ebrRatio<0.6){hClass2='health-over';hLabel2='⚠ Not enough senior time';}
            else if(ebrRatio<0.8){hClass2='health-approaching';hLabel2='Not enough senior time?';}
            else{hClass2='health-on-target';hLabel2='Healthy mix';}
            tipText2='EBR $'+Math.round(ebr)+'/hr · firm target $'+Math.round(targetEBR)+'/hr';
            phHealthHtml='<span class="phase-health '+hClass2+'">● '+hLabel2+'<span class="ph-tooltip">'+tipText2+'</span></span>';
          }
        } else if(phData.contractedFee>0&&phData.feeExpended!=null){
          // Fee-based over/under is the PRIMARY Fixed-Fee signal, shown whenever there's a real
          // contracted fee — independent of whether Hours Budget could even be computed (some
          // phases, Discretionary Approvals in particular, have no Blended Rate Rollup to derive
          // one from). Mirrors the same fix in TimeReviewModal.jsx (2026-08-08).
          var hasHours=phData.hoursBudget!=null&&phData.hoursBudget>0&&phData.hoursExpended!=null;
          var feeOver=phData.feeExpended-phData.contractedFee;
          var isFeeOver=feeOver>0;
          var pct=phData.phaseWorkRemaining!=null?Math.round(phData.phaseWorkRemaining*100):null;
          var hClass=isFeeOver?'health-over':'health-on-target';
          var hLabel=isFeeOver?'⚠ Over budget by $'+Math.round(feeOver).toLocaleString('en-US'):'$'+Math.round(phData.contractedFee-phData.feeExpended).toLocaleString('en-US')+' of fee remaining';
          var tipText=hasHours?(phData.hoursExpended.toFixed(1)+' of '+phData.hoursBudget.toFixed(1)+' hrs used'):'';
          // "schedule:" prefix, not bare "% remaining" — a different axis from the $ figure above
          // it, and without the label that distinction is easy to lose on re-read later (mirrors
          // TimeReviewModal.jsx, 2026-08-08).
          if(pct===0) tipText+=(tipText?' · ':'')+'schedule: all milestones posted';
          else if(pct!==null) tipText+=(tipText?' · ':'')+'schedule: '+pct+'% of milestones still open';
          phHealthHtml='<span class="phase-health '+hClass+'">● '+hLabel+'<span class="ph-tooltip">'+tipText+'</span></span>';
        }
      }
      html+='<div class="entry-phase">'+phase+phHealthHtml+'</div>';
    } else if(project){
      html+='<div class="entry-phase" style="color:var(--red);font-weight:600;font-style:normal">⚠ No phase assigned — edit to fix</div>';
    }
    if(memo&&!isEditing) html+='<div class="entry-memo">'+memo+'</div>';
    if(!isEditing){
      var dEntType=(Array.isArray(f['Entity Type'])?f['Entity Type'][0]:f['Entity Type'])||'';
      var dEntName=(Array.isArray(f['Entity Name'])?f['Entity Name'][0]:f['Entity Name'])||'';
      if(dEntType){
        var dab=ENT_ABBR[dEntType]||dEntType;
        html+='<div style="display:inline-flex;align-items:center;gap:5px;margin-top:3px;font-family:\'DM Mono\',monospace;font-size:10px;color:#5a7a86;">'
          +'<span style="background:#07272D12;color:#07272D;border-radius:5px;padding:1px 6px;font-weight:600;">'+dab+'</span>'
          +(dEntName?'<span>'+dEntName+'</span>':'')+'</div>';
      }
      // Skill credit preview — what demonstrated skill(s) these hours build.
      var skCredit=skillsForTimeEntry({activityCode:actCode, entityType:dEntType, entityName:dEntName, phaseName:f['Phase Name'], hours:hours});
      if(skCredit.length){
        html+='<div style="margin-top:3px;font-family:\'DM Mono\',monospace;font-size:10px;color:#8a9ba3;">Skill credit: +'+Number(hours).toFixed(1)+'h &rarr; '+skCredit.map(function(c){return c.skill;}).join(', ')+'</div>';
      }
    }
    if(!isEditing&&(isUnsubView||statusLabel==='Pending Review'||statusLabel==='Rejected')){
      if(conf==='medium'||conf==='low'){
        var bt=conf==='medium'?'⚠ Claude is moderately confident about this entry.':'⚠ Claude has low confidence about this entry.';
        var tt=revNotes?'<span class="rn-tooltip">'+revNotes+'</span>':'';
        html+='<div class="review-note">'+bt+tt+'</div>';
      } else if(revNotes){ html+='<div class="review-note-subtle">'+revNotes+'</div>'; }
    }
    html+='</div>';
    html+='<div class="entry-meta"><span class="entry-hours">'+hours.toFixed(2)+' hrs</span>';
    if(actCode) html+='<span class="entry-badge '+(billable?'badge-billable':'badge-overhead')+'">'+actCode+'</span>';
    html+='<span style="display:inline-flex;align-items:center;gap:3px;font-size:10px;font-weight:600;color:'+(billable?'var(--green)':'var(--text-soft)')+';">'
      +'<span style="width:7px;height:7px;border-radius:50%;background:'+(billable?'var(--green)':'var(--border)')+'"></span>'
      +(billable?'Billable':'Non-billable')+'</span>';
    html+='</div></div>';

    if(isEditing){
      var cActId='';
      var asr=f['Activity Code String'],as2=(asr?(Array.isArray(asr)?String(asr[0]||''):String(asr)):'' ).trim();
      if(as2){var mb=ACTIVITY_CODES.find(function(a){return a.label.trim()===as2;});if(mb)cActId=mb.id;}
      if(!cActId){var are=f['Activity Code'];if(are){if(are.id)cActId=are.id;else if(Array.isArray(are)&&are[0])cActId=are[0].id||are[0];}}
      var cPhId='',pr=f['Phase Airtable ID'];
      if(pr){if(Array.isArray(pr)&&pr[0])cPhId=pr[0].id||pr[0];else if(pr.id)cPhId=pr.id;else if(typeof pr==='string')cPhId=pr;}
      if(!cPhId&&phase&&allPhases.length){var mp=allPhases.find(function(p){return p.name===phase;});if(mp)cPhId=mp.id;}
      var cBil=f['Billable']===true;
      var cPName=(allPhases.find(function(p){return p.id===cPhId;})||{name:''}).name;
      var cALabel=(ACTIVITY_CODES.find(function(a){return a.id===cActId;})||{label:''}).label;
      // Current entity attribution (Work item) for the edit picker.
      var cEntId=(Array.isArray(f['Entity Id'])?f['Entity Id'][0]:f['Entity Id'])||'';
      var cEntType=(Array.isArray(f['Entity Type'])?f['Entity Type'][0]:f['Entity Type'])||'';
      var cEntName=(Array.isArray(f['Entity Name'])?f['Entity Name'][0]:f['Entity Name'])||'';
      var cEntDisplay=cEntType==='Untied'?('Not tied to one milestone: '+cEntName):cEntId?('['+(ENT_ABBR[cEntType]||cEntType||'Item')+'] '+(cEntName||cEntId)):'';
      if(cEntType==='Untied') cEntId='__untied';
      html+='<div class="edit-form">';
      html+='<div class="edit-field"><label>Actual Hours</label><input type="number" id="edit-hours-'+rid+'" value="'+hours+'" step="0.1" min="0" max="24"></div>';
      html+='<div class="edit-field"><label>Phase</label><div class="sd-wrap" data-sd="phase-'+rid+'" data-selected-id="'+cPhId+'">';
      html+='<input class="sd-input" id="sd-input-phase-'+rid+'" type="text" placeholder="Search phases..." autocomplete="off" value="'+cPName.replace(/"/g,'&quot;')+'">';
      html+='<span class="sd-arrow">&#9660;</span></div></div>';
      html+='<div class="edit-field"><label>Milestone</label><div class="sd-wrap" data-sd="entity-'+rid+'" data-selected-id="'+cEntId+'">';
      html+='<input class="sd-input" id="sd-input-entity-'+rid+'" type="text" placeholder="Which milestone was this for?" autocomplete="off" value="'+cEntDisplay.replace(/"/g,'&quot;')+'">';
      html+='<span class="sd-arrow">&#9660;</span></div></div>';
      html+='<div class="edit-field"><label>Activity Code</label><div class="sd-wrap" data-sd="activity-'+rid+'" data-selected-id="'+cActId+'">';
      html+='<input class="sd-input" id="sd-input-activity-'+rid+'" type="text" placeholder="Search activity codes..." autocomplete="off" value="'+cALabel.replace(/"/g,'&quot;')+'">';
      html+='<span class="sd-arrow">&#9660;</span></div></div>';
      html+='<div class="edit-field" style="flex-direction:row;align-items:center;gap:10px">';
      html+='<label style="margin:0;text-transform:uppercase;font-size:11px;font-weight:600;color:var(--text-soft)">Billable</label>';
      html+='<input type="checkbox" id="edit-billable-'+rid+'"'+(cBil?' checked':'')+' style="width:16px;height:16px;cursor:pointer"></div>';
      html+='<div class="edit-field"><label>Memo</label><textarea id="edit-memo-'+rid+'">'+memo+'</textarea></div>';
      html+='<div class="edit-form-actions"><button class="btn btn-save" data-action="save" data-record-id="'+rid+'">Save</button>';
      html+='<button class="btn btn-cancel" data-action="cancel">Cancel</button>';
      html+='<span class="feedback-msg" id="feedback-edit-'+rid+'"></span></div></div>';
    }

    html+='<div class="entry-actions">';
    var rn=f['Rejection Notes']||'';
    var pt=(statusLabel==='Rejected'&&rn)?'<span class="sp-tooltip">'+rn.replace(/"/g,'&quot;')+'</span>':'';
    // Self-approvers (Round 16.4 — no reviewer ever sees this entry) get the real BQE Core error on
    // their own Post Failed pill, same parse TimeReviewModal.jsx uses, so they know what to fix
    // without needing to open the manager Time Review tool.
    if(statusLabel==='Post Failed'&&canSelfApprove){
      var peRaw=f['Post Error']||'';
      var peType='Post error';
      if(peRaw){
        var tm=peRaw.match(/"type"\s*:\s*"([^"]+)"/);
        if(tm) peType=tm[1];
        else if(peRaw.indexOf('HTTP 4')!==-1||peRaw.indexOf('HTTP 5')!==-1){ var cm=peRaw.match(/HTTP (\d+)/); peType=cm?'HTTP '+cm[1]+' error':'Post error'; }
      }
      pt='<span class="sp-tooltip">'+(peRaw?(peType+': '+peRaw):peType).replace(/"/g,'&quot;')+'</span>';
    }
    html+='<span class="status-pill '+statusPillClass(statusLabel)+'">'+statusLabel+pt+'</span>';
    if(isEditing){
      /* While editing, only the edit form's Save / Cancel show — suppress Submit / Delete here so an
         entry can't be submitted or deleted before the edit is saved. */
    } else if(locked){
      html+='<span class="action-note" style="color:var(--text-soft)">&#128274; Locked — ask an admin to reopen</span>';
    } else if(statusLabel==='Pending Review'){
      if(!isEditing) html+='<button class="btn btn-outline" data-action="edit" data-record-id="'+rid+'">Edit</button>';
      if(confirmingSubmitId===rid){
        html+='<div class="delete-confirm" style="border-color:var(--slate);background:var(--white)">';
        html+='<span style="font-size:11px;font-weight:600;color:var(--red)">By submitting you attest this entry is accurate.'+(canSelfApprove?' It will be approved and posted to BQE Core.':'')+'</span>';
        html+='<button class="btn btn-primary" data-action="submit-confirm" data-record-id="'+rid+'">Confirm</button>';
        html+='<button class="btn btn-cancel" data-action="submit-cancel">Cancel</button></div>';
      } else if(codeWhy){ html+=codeWhyHtml; }
      else { html+='<button class="btn btn-primary" data-action="submit-prompt" data-record-id="'+rid+'">'+(canSelfApprove?'Submit &amp; post':'Submit')+'</button>'; }
      if(confirmingDeleteId===rid){
        html+='<div class="delete-confirm">Delete this entry?';
        html+='<button class="btn btn-danger-solid" data-action="delete-confirm" data-record-id="'+rid+'">Delete</button>';
        html+='<button class="btn btn-cancel" data-action="delete-cancel">Cancel</button></div>';
      } else { html+='<button class="btn btn-danger" data-action="delete-prompt" data-record-id="'+rid+'">Delete</button>'; }
      html+='<span class="feedback-msg" id="feedback-status-'+rid+'"></span>';
    } else if(statusLabel==='Submitted'){
      html+='<span class="action-note">Awaiting review</span>';
    } else if(statusLabel==='Rejected'){
      if(!isEditing) html+='<button class="btn btn-outline" data-action="edit" data-record-id="'+rid+'">Edit</button>';
      if(confirmingSubmitId===rid){
        html+='<div class="delete-confirm" style="border-color:var(--slate);background:var(--white)">';
        html+='<span style="font-size:11px;font-weight:600;color:var(--red)">By resubmitting you attest this entry is accurate.</span>';
        html+='<button class="btn btn-primary" data-action="submit-confirm" data-record-id="'+rid+'">Confirm</button>';
        html+='<button class="btn btn-cancel" data-action="submit-cancel">Cancel</button></div>';
      } else if(codeWhy){ html+=codeWhyHtml; }
      else { html+='<button class="btn btn-primary" data-action="submit-prompt" data-record-id="'+rid+'">Resubmit</button>'; }
      if(confirmingDeleteId===rid){
        html+='<div class="delete-confirm">Delete this entry?';
        html+='<button class="btn btn-danger-solid" data-action="delete-confirm" data-record-id="'+rid+'">Delete</button>';
        html+='<button class="btn btn-cancel" data-action="delete-cancel">Cancel</button></div>';
      } else { html+='<button class="btn btn-danger" data-action="delete-prompt" data-record-id="'+rid+'">Delete</button>'; }
      html+='<span class="feedback-msg" id="feedback-status-'+rid+'"></span>';
    } else if(statusLabel==='Approved'){
      html+='<span class="action-note" style="color:var(--green)">&#10003; Approved</span>';
    } else if(statusLabel==='Post Failed'){
      if(canSelfApprove){
        // Self-approvers posted this entry themselves with no reviewer in the loop, so — unlike a
        // regular staff member's Post Failed entry, which a manager already approved and should stay
        // in their queue — fixing and retrying it here doesn't bypass anyone's review.
        if(!isEditing) html+='<button class="btn btn-outline" data-action="edit" data-record-id="'+rid+'">Edit</button>';
        html+='<button class="btn btn-primary" data-action="retry-post" data-record-id="'+rid+'">Retry post</button>';
        if(confirmingDeleteId===rid){
          html+='<div class="delete-confirm">Delete this entry?';
          html+='<button class="btn btn-danger-solid" data-action="delete-confirm" data-record-id="'+rid+'">Delete</button>';
          html+='<button class="btn btn-cancel" data-action="delete-cancel">Cancel</button></div>';
        } else { html+='<button class="btn btn-danger" data-action="delete-prompt" data-record-id="'+rid+'">Delete</button>'; }
        html+='<span class="feedback-msg" id="feedback-status-'+rid+'"></span>';
      } else {
        html+='<span class="action-note" style="color:var(--amber)">⚠ Failed to post to BQE — your manager will retry</span>';
      }
    } else if(statusLabel==='Posted'){
      html+='<span class="action-note">Posted to BQE</span>';
    }
    html+='</div></div></div>';
    return html;
  }

  // ── Events ──
  function gid(id){ return document.getElementById(id); }
  function bindEvents(){
    container.querySelectorAll('.day-cell').forEach(function(c){
      c.addEventListener('click',function(){selectedDate=this.getAttribute('data-date');editingRecordId=null;confirmingDeleteId=null;selectedBulkIds={};bulkDeleteConfirming=false;bulkSubmitConfirming=false;render();});
    });
    var pw=gid('aj-prev-week'),nw=gid('aj-next-week'),tw=gid('aj-this-week');
    if(pw) pw.addEventListener('click',function(){weekOffset--;selectedDate=null;editingRecordId=null;confirmingDeleteId=null;selectedBulkIds={};bulkDeleteConfirming=false;bulkSubmitConfirming=false;render();});
    if(nw) nw.addEventListener('click',function(){weekOffset++;selectedDate=null;editingRecordId=null;confirmingDeleteId=null;selectedBulkIds={};bulkDeleteConfirming=false;bulkSubmitConfirming=false;render();});
    if(tw) tw.addEventListener('click',function(){weekOffset=0;selectedDate=formatDate(new Date());editingRecordId=null;confirmingDeleteId=null;render();});
    var vw=gid('aj-view-week'), vu=gid('aj-view-unsub'), vr=gid('aj-view-rejected');
    if(vw) vw.addEventListener('click',function(){unsubmittedView=false;rejectedOnly=false;selectedBulkIds={};bulkSubmitConfirming=false;render();});
    if(vu) vu.addEventListener('click',function(){unsubmittedView=true;rejectedOnly=false;selectedBulkIds={};bulkSubmitConfirming=false;render();});
    if(vr) vr.addEventListener('click',function(){unsubmittedView=true;rejectedOnly=true;selectedBulkIds={};bulkSubmitConfirming=false;render();});
    var mt=gid('aj-month-toggle');
    if(mt) mt.addEventListener('click',function(){monthViewOpen=!monthViewOpen;render();});
    var mp=gid('aj-month-prev'),mn=gid('aj-month-next');
    if(mp) mp.addEventListener('click',function(e){e.stopPropagation();monthOffset--;render();});
    if(mn) mn.addEventListener('click',function(e){e.stopPropagation();monthOffset++;render();});
    container.querySelectorAll('[data-month-date]').forEach(function(c){
      c.addEventListener('click',function(){
        var ds=this.getAttribute('data-month-date');selectedDate=ds;
        var clicked=new Date(ds+'T00:00:00'),thisM=getMonday(new Date());
        weekOffset=Math.round((getMonday(clicked)-thisM)/(7*24*3600*1000));
        monthViewOpen=false;editingRecordId=null;confirmingDeleteId=null;render();
      });
    });
    var sc=gid('aj-search-clear');
    if(sc) sc.addEventListener('click',function(){searchQuery='';render();var s=gid('aj-search');if(s)s.focus();});
    var si=gid('aj-search');
    if(si) si.addEventListener('input',function(){searchQuery=this.value;var p=this.selectionStart;render();var el=gid('aj-search');if(el){el.focus();el.setSelectionRange(p,p);}});
    var nb=gid('aj-new-entry-btn');
    if(nb) nb.addEventListener('click',function(){intakePanelOpen=!intakePanelOpen;intakeSubmitting=false;render();if(intakePanelOpen){var ta=gid('intake-narrative');if(ta)ta.focus();}});
    var cb=gid('intake-cancel-btn');
    if(cb) cb.addEventListener('click',function(){intakePanelOpen=false;intakeSubmitting=false;render();});
    var mgb=gid('mgphase-toggle-btn');
    if(mgb) mgb.addEventListener('click',function(){ toggleManagePhases(); });
    var idEl=gid('intake-date');
    if(idEl) idEl.addEventListener('input',function(){
      var cf=gid('intake-date-confirm');
      if(cf) cf.textContent=formatDateConfirm(this.value);
    });
    var sb=gid('intake-submit-btn');
    if(sb) sb.addEventListener('click',function(){
      var dEl=gid('intake-date'),nEl=gid('intake-narrative'),fEl=gid('intake-feedback');
      var dv=dEl?dEl.value.trim():'',nv=nEl?nEl.value.trim():'';
      if(!dv){fEl.className='feedback-msg feedback-err';fEl.textContent='Please select a date';fEl.style.display='inline-block';return;}
      if(!nv){fEl.className='feedback-msg feedback-err';fEl.textContent='Please enter a work description';fEl.style.display='inline-block';return;}
      if(isLocked(dv.slice(0,10))){fEl.className='feedback-msg feedback-err';fEl.textContent='That day is locked — ask an admin to reopen it';fEl.style.display='inline-block';return;}
      var sid2=getSubmitterId();
      if(!sid2){fEl.className='feedback-msg feedback-err';fEl.textContent='Could not identify submitter — please reload';fEl.style.display='inline-block';return;}
      intakeSubmitting=true;render();
      tc.submitIntake(dv,nv,sid2)
        .then(function(result){
          if(result.success){
            intakePanelOpen=false;intakeSubmitting=false;
            parsingState=null;
            render();
            setTimeout(function(){fetchRecords(function(recs){allRecords=recs;render();});},1200);
            fireGateRecheck();
          } else {
            intakeSubmitting=false;
            var fb=gid('intake-feedback');
            if(fb){fb.className='feedback-msg feedback-err';fb.textContent=(result.error||'Parse failed — try again');fb.style.display='inline-block';}
            var sb2=gid('intake-submit-btn');if(sb2){sb2.disabled=false;sb2.textContent='Submit';}
          }
        })
        .catch(function(){
          intakeSubmitting=false;
          var fb=gid('intake-feedback');if(fb){fb.className='feedback-msg feedback-err';fb.textContent='Network error — try again';fb.style.display='inline-block';}
          var sb2=gid('intake-submit-btn');if(sb2){sb2.disabled=false;sb2.textContent='Submit';}
        });
    });
    initSearchableDropdowns();
    initManagePhasesSearch();
  }

  // ── Action handler ──
  function handleAction(e){
    var btn=e.target.closest('[data-action]'); if(!btn||!container.contains(btn)) return;
    e.stopPropagation();
    var action=btn.getAttribute('data-action'), rid=btn.getAttribute('data-record-id');

    // Enforcement-gate lock: ignore edit/submit/delete on a locked day's entries.
    var MUTATING={'edit':1,'save':1,'submit-prompt':1,'submit-confirm':1,'delete-prompt':1,'delete-confirm':1,'bulk-toggle':1};
    if(rid&&MUTATING[action]&&recordLocked(rid)) return;

    if(action==='goto-day'){
      var gd=btn.getAttribute('data-date'); if(!gd) return;
      selectedDate=gd;
      weekOffset=Math.round((getMonday(new Date(gd+'T00:00:00'))-getMonday(new Date()))/(7*24*3600*1000));
      monthViewOpen=false;editingRecordId=null;confirmingDeleteId=null;render();
      return;
    }

    if(action==='bulk-select-all'){
      var ids=btn.getAttribute('data-ids').split(',').filter(Boolean);
      ids.forEach(function(id){selectedBulkIds[id]=btn.checked;});
      if(!btn.checked) bulkDeleteConfirming=false;
      render(); return;
    }
    if(action==='bulk-toggle'){
      selectedBulkIds[rid]=btn.checked;
      if(!btn.checked) bulkDeleteConfirming=false;
      render(); return;
    }
    if(action==='bulk-submit-prompt'){ bulkSubmitConfirming=true; render(); return; }
    if(action==='bulk-submit-cancel'){ bulkSubmitConfirming=false; render(); return; }
    if(action==='bulk-submit-confirm'){
      var ids=Object.keys(selectedBulkIds).filter(function(k){return selectedBulkIds[k];});
      var today=todayStr();
      // Leave out entries whose activity code doesn't fit their project — they can't be submitted.
      var badCode=ids.filter(function(id){ var r=allRecords.find(function(x){return x.id===id;}); return r&&tc.activityCodeProblem(r.fields['Phase Name'], r.fields['Activity Code String']); });
      // Project time needs its milestone (api/time-entries.js refuses the submit without one).
      var noMs=ids.filter(function(id){ var r=allRecords.find(function(x){return x.id===id;}); return r&&badCode.indexOf(id)===-1&&tc.entryNeedsMilestone(r.fields); });
      if(noMs.length){
        ids=ids.filter(function(id){ return noMs.indexOf(id)===-1; });
        alertDialog({ title: noMs.length+' entr'+(noMs.length===1?'y':'ies')+' not submitted', message: 'Project time needs the milestone it was for. Edit '+(noMs.length===1?'it':'them')+', pick the milestone, and submit again.' });
      }
      if(badCode.length){
        ids=ids.filter(function(id){ return badCode.indexOf(id)===-1; });
        alertDialog({ title: badCode.length+' entr'+(badCode.length===1?'y':'ies')+' not submitted', message: 'Each needs a phase (time for a project not set up yet goes to Business Development) and an activity code that fits it (Alder James Overhead takes OP- or PTO- codes; every other project takes AS- codes). Edit '+(badCode.length===1?'it':'them')+' and submit again.' });
      }
      ids.forEach(function(id){
        // Every submit stamps the date, a resubmitted fix too: the reviewer's lockout waits a working
        // day from it (api/time-gate.js reviewReadyBefore), so a fix made today comes up tomorrow.
        var patchFields={'Status':'Submitted','Submitted At':today};
        updateLocalStatus(id, canSelfApprove?'Approved':'Submitted');
        var p=atPatch(id,patchFields);
        if(canSelfApprove) p=p.then(function(){ return selfApproveAndPost(id); });
        p.catch(function(){});
      });
      selectedBulkIds={}; bulkDeleteConfirming=false; bulkSubmitConfirming=false; lastStatusChange=Date.now();
      render(); scheduleReload(8000); fireGateRecheck(); return;
    }
    if(action==='bulk-delete-prompt'){ bulkDeleteConfirming=true; render(); return; }
    if(action==='bulk-delete-cancel'){ bulkDeleteConfirming=false; render(); return; }
    if(action==='bulk-delete-confirm'){
      var ids=Object.keys(selectedBulkIds).filter(function(k){return selectedBulkIds[k];});
      ids.forEach(function(id){
        allRecords=allRecords.filter(function(r){return r.id!==id;});
        atDelete(id).catch(function(){});
      });
      selectedBulkIds={}; bulkDeleteConfirming=false; lastStatusChange=Date.now();
      render(); return;
    }
    if(action==='bulk-clear'){ selectedBulkIds={}; bulkDeleteConfirming=false; bulkSubmitConfirming=false; render(); return; }

    if(action==='edit'){ editingRecordId=rid; render(); }
    else if(action==='cancel'){ editingRecordId=null; render(); }
    else if(action==='save'){
      var hi=gid('edit-hours-'+rid);
      var mi=gid('edit-memo-'+rid);
      var pw=container.querySelector('[data-sd="phase-'+rid+'"]');
      var aw=container.querySelector('[data-sd="activity-'+rid+'"]');
      var ew=container.querySelector('[data-sd="entity-'+rid+'"]');
      var bi=gid('edit-billable-'+rid);
      var fe=gid('feedback-edit-'+rid);
      var nh=parseFloat(hi.value), nm=mi.value.trim();
      var nph=pw?pw.getAttribute('data-selected-id')||'':'';
      var nac=aw?aw.getAttribute('data-selected-id')||'':'';
      var nbil=bi.checked;
      // Entity attribution: resolve picked work item → type/name; only send when it actually changed.
      // (handleAction has no `f` in scope — look the record up by rid.)
      var sRec=allRecords.find(function(x){return x.id===rid;}), sf=(sRec&&sRec.fields)||{};
      var prevEntId=(Array.isArray(sf['Entity Id'])?sf['Entity Id'][0]:sf['Entity Id'])||'';
      var nEnt=ew?ew.getAttribute('data-selected-id')||'':'';
      var entChanged=(nEnt||'')!==(prevEntId||'');
      var nEntType='',nEntName='';
      if(nEnt==='__untied'){ nEntType='Untied'; nEntName=(ew&&ew.getAttribute('data-untied-reason'))||(sf['Entity Type']==='Untied'?sf['Entity Name']:'')||''; }
      else if(nEnt){ var wi=myWorkItemsById[nEnt]; nEntType=wi?wi.type:''; nEntName=wi?wi.ename:''; }
      if(nEnt==='__untied'&&sf['Entity Type']==='Untied'&&nEntName===sf['Entity Name']) entChanged=false;
      if(isNaN(nh)||nh<0){fe.className='feedback-msg feedback-err';fe.textContent='Enter a valid hours value';fe.style.display='inline-block';return;}
      fe.className='feedback-msg feedback-pending'; fe.innerHTML='Saving<span class="aj-dots"></span>'; fe.style.display='inline-block';
      var locUpd={'Actual Hours':nh,'Memo Draft':nm,'Activity Code':nac,'Billable':nbil,'Phase Airtable ID':nph};
      if(entChanged){ locUpd['Entity Type']=nEntType; locUpd['Entity Id']=nEnt; locUpd['Entity Name']=nEntName; }
      updateLocalFields(rid,locUpd);
      editingRecordId=null;
      var editPayload={hours:nh,memo:nm,billable:nbil,activityId:nac,phaseId:nph};
      if(entChanged){ editPayload.entityType=nEntType; editPayload.entityId=nEnt==='__untied'?'':nEnt; editPayload.entityName=nEntName; }
      tc.saveEntryEdit(rid,editPayload)
        .then(function(res){
          if(res&&res.id){
            fe.className='feedback-msg feedback-ok'; fe.textContent='Saved';
            setTimeout(function(){render();},900);
            scheduleReload(5000);
          } else {
            fe.className='feedback-msg feedback-err'; fe.textContent='Error — try again';
          }
        })
        .catch(function(){
          fe.className='feedback-msg feedback-err'; fe.textContent='Error — try again';
        });
    }
    else if(action==='submit-prompt'){ confirmingSubmitId=rid; confirmingDeleteId=null; render(); }
    else if(action==='submit-cancel'){ confirmingSubmitId=null; render(); }
    else if(action==='submit-confirm'){
      var rec=allRecords.find(function(x){return x.id===rid;});
      confirmingSubmitId=null; lastStatusChange=Date.now();
      updateLocalStatus(rid, canSelfApprove?'Approved':'Submitted'); render();
      var extra={'Submitted At':todayStr()};   // every submit, a resubmitted fix too (see above)
      tc.setEntryStatus(rid,'Submitted',extra)
        .then(function(){ return canSelfApprove ? selfApproveAndPost(rid) : null; })
        .then(function(){ scheduleReload(8000); fireGateRecheck(); })
        .catch(function(err){
          updateLocalStatus(rid, rec?getStatusLabel(rec):'Pending Review'); render();
          if(err&&err.message) alertDialog({ title: 'Not submitted', message: err.message });
        });
    }
    else if(action==='retry-post'){
      var rpFs=gid('feedback-status-'+rid);
      btn.disabled=true; btn.textContent='Retrying…';
      if(rpFs){ rpFs.className='feedback-msg feedback-pending'; rpFs.innerHTML='Sending to BQE Core<span class="aj-dots"></span>'; rpFs.style.display='inline-block'; }
      lastStatusChange=Date.now();
      tc.postTimeEntryToCore(rid).then(function(result){
        if(result&&result.success){
          updateLocalFields(rid,{'Status':'Posted','Post Error':''});
          lastStatusChange=Date.now();
          render();
          scheduleReload(2500);
        } else {
          btn.disabled=false; btn.textContent='Retry post';
          if(rpFs){ rpFs.className='feedback-msg feedback-err'; rpFs.textContent='Failed again — edit and retry'; }
        }
      }).catch(function(){
        btn.disabled=false; btn.textContent='Retry post';
        if(rpFs){ rpFs.className='feedback-msg feedback-err'; rpFs.textContent='Network error — retry'; }
      });
    }
    else if(action==='delete-prompt'){ confirmingDeleteId=rid; render(); }
    else if(action==='delete-cancel'){ confirmingDeleteId=null; render(); }
    else if(action==='delete-confirm'){
      btn.style.opacity='0.6'; btn.style.transform='scale(0.96)';
      setTimeout(function(){
        confirmingDeleteId=null;
        allRecords=allRecords.filter(function(r){return r.id!==rid;});
        render();
        atDelete(rid).catch(function(){});
      }, 200);
    }
  }

  // ── Searchable dropdowns ──
  var activeSD=null;
  function buildSDList(sdId,items,query){
    var wrap=container.querySelector('[data-sd="'+sdId+'"]'); if(!wrap) return;
    var ex=wrap.querySelector('.sd-list'); if(ex) ex.remove();
    var q=(query||'').toLowerCase().trim();
    var filtered=items.filter(function(i){return !q||i.special||i.name.toLowerCase().indexOf(q)!==-1;});
    var list=document.createElement('div'); list.className='sd-list';
    if(sdId.startsWith('phase-')||sdId.startsWith('entity-')){
      var no=document.createElement('div');no.className='sd-option'+(wrap.getAttribute('data-selected-id')?'':' sd-selected');no.textContent=sdId.startsWith('entity-')?'— No milestone —':'— No phase —';
      no.addEventListener('mousedown',function(e){e.preventDefault();wrap.setAttribute('data-selected-id','');wrap.querySelector('.sd-input').value='';closeSD();});
      list.appendChild(no);
    }
    if(filtered.length===0){var em=document.createElement('div');em.className='sd-empty';em.textContent='No matches';list.appendChild(em);}
    else{filtered.forEach(function(item){
      var opt=document.createElement('div'),sid2=wrap.getAttribute('data-selected-id');
      opt.className='sd-option'+(item.id===sid2?' sd-selected':'');opt.textContent=item.name;
      if(item.special) opt.className+=' sd-special';
      opt.addEventListener('mousedown',function(e){e.preventDefault();if(item.special){closeSD();item.special(wrap);return;}wrap.setAttribute('data-selected-id',item.id);wrap.querySelector('.sd-input').value=item.name;closeSD();});
      list.appendChild(opt);
    });}
    wrap.appendChild(list);
  }
  function closeSD(){
    if(activeSD){var w=container.querySelector('[data-sd="'+activeSD+'"]');if(w){var l=w.querySelector('.sd-list');if(l)l.remove();}activeSD=null;}
  }
  // Work items are scoped to the entry's project — you can only attribute to work on the project you
  // logged against (an overhead/BD phase has no project, so no work items). Match on readable name.
  function entityItemsFor(sdId){
    var rid=sdId.slice(7);   // 'entity-'.length
    var rec=allRecords.find(function(x){return x.id===rid;});
    if(!rec) return [];
    var scoped=tc.scopeWorkItemsForEntry(myWorkItems, { projectName: rec.fields['Project Name'], phaseName: rec.fields['Phase Name'] });
    var out=scoped.map(function(it){
      var ab=ENT_ABBR[it.entity_type]||it.entity_type;
      return { id: it.entity_id, name: '['+ab+'] '+(it.entity_name||it.entity_id)+(it.project_name?' · '+it.project_name:'') };
    });
    // Every open milestone on the entry's project, its phase first (Matt, 2026-10-24: the time card
    // collects the milestone; the crewed ones above are only the most likely).
    var uid=Array.isArray(rec.fields['Project Record UID'])?rec.fields['Project Record UID'][0]:rec.fields['Project Record UID'];
    var ph=Array.isArray(rec.fields['Phase Airtable ID'])?rec.fields['Phase Airtable ID'][0]:rec.fields['Phase Airtable ID'];
    if(uid&&!projMilestones[uid]){
      projMilestones[uid]='loading';
      tc.getProjectMilestones(uid, ph).then(function(list){
        projMilestones[uid]=list;
        list.forEach(function(m){ if(!myWorkItemsById[m.id]) myWorkItemsById[m.id]={ type:'Deliverable', ename:m.name }; });
        if(activeSD===sdId){ var inp=container.querySelector('#sd-input-'+sdId); buildSDList(sdId, entityItemsFor(sdId), inp?inp.value:''); }
      });
    }
    var have={}; out.forEach(function(o){have[o.id]=1;});
    if(Array.isArray(projMilestones[uid])) projMilestones[uid].forEach(function(m){ if(!have[m.id]) out.push({ id:m.id, name:'[Milestone] '+m.name+(m.phase?' · '+m.phase:'') }); });
    // When the one you need isn't there: say it wasn't any one milestone. The time card never
    // creates threads, RFIs or submittals (Matt, 2026-11-03, removing the 2026-10-24 "Something new"):
    // a thread made there carried one line and none of the memo, and nobody knew where it came from.
    if(uid){
      out.push({ id:'__untied', name:'Not tied to one milestone…', special:function(wrap){
        promptDialog({ title:'Not tied to one milestone', message:'Use this rarely: time that was about the project but not any one milestone (a team meeting, a general call). Say what it was.', placeholder:'Monday project meeting', required:true }).then(function(reason){
          if(!reason) return;
          wrap.setAttribute('data-selected-id','__untied'); wrap.setAttribute('data-untied-reason',reason);
          var inp=wrap.querySelector('.sd-input'); if(inp) inp.value='Not tied to one milestone: '+reason;
        });
      } });
    }
    return out;
  }
  function initSearchableDropdowns(){
    container.querySelectorAll('.sd-wrap').forEach(function(wrap){
      var sdId=wrap.getAttribute('data-sd'),input=wrap.querySelector('.sd-input'); if(!input) return;
      if(sdId==='mgphase-project') return;   // separate, non-form-field search — see initManagePhasesSearch
      // Compute items at focus/input time (a getter), so a late-loading work-item list is reflected
      // without re-rendering the form.
      var itemsFn=sdId.startsWith('phase-')?function(){return allPhases}
        :(sdId.startsWith('entity-')?function(){return entityItemsFor(sdId)}
        :function(){return ACTIVITY_CODES.map(function(ac){return{id:ac.id,name:ac.label};});});
      input.addEventListener('focus',function(){closeSD();activeSD=sdId;buildSDList(sdId,itemsFn(),'');this.select();});
      input.addEventListener('input',function(){buildSDList(sdId,itemsFn(),this.value);});
    });
  }
  // Manager/lead "Manage phases" project search — deliberately separate from the generic searchable-
  // dropdown system above: those pickers SELECT a value into a form field for the entry being edited;
  // this one performs an immediate action (open the phase landscape for whichever project is picked),
  // so it needs its own click behavior rather than the shared "set data-selected-id and fill the input"
  // handler in buildSDList.
  function initManagePhasesSearch(){
    var wrap=container.querySelector('[data-sd="mgphase-project"]'); if(!wrap) return;
    var input=wrap.querySelector('.sd-input'); if(!input) return;
    function buildList(query){
      var ex=wrap.querySelector('.sd-list'); if(ex) ex.remove();
      var q=(query||'').toLowerCase().trim();
      var items=(managePhasesProjects||[]).map(function(p){
        var num=p.fields['Project Number']||'', nm=p.fields['Project Name']||'';
        return { id:p.id, name:(num?num+' - ':'')+nm };
      });
      var filtered=items.filter(function(i){return !q||i.name.toLowerCase().indexOf(q)!==-1;});
      var list=document.createElement('div'); list.className='sd-list';
      if(managePhasesProjects===null||(managePhasesProjects.length===0)){
        var ld=document.createElement('div'); ld.className='sd-empty'; ld.textContent='Loading projects…'; list.appendChild(ld);
      } else if(filtered.length===0){
        var em=document.createElement('div'); em.className='sd-empty'; em.textContent='No matches'; list.appendChild(em);
      } else {
        filtered.slice(0,25).forEach(function(item){
          var opt=document.createElement('div'); opt.className='sd-option'; opt.textContent=item.name;
          opt.addEventListener('mousedown',function(e){
            e.preventDefault();
            managePhasesOpen=false; managePhasesQuery='';
            openPhaseLandscape(item.id,item.name);
            render();
          });
          list.appendChild(opt);
        });
      }
      wrap.appendChild(list);
    }
    input.addEventListener('focus',function(){buildList(this.value);});
    input.addEventListener('input',function(){managePhasesQuery=this.value;buildList(this.value);});
    if(document.activeElement===input) buildList(input.value);
  }

  // Manage phases opens from the Time Card's title bar (an event, since the bar is React) and closes
  // from its own Close.
  function toggleManagePhases(){
    managePhasesOpen=!managePhasesOpen; managePhasesQuery='';
    if(managePhasesOpen&&managePhasesProjects===null){
      managePhasesProjects=[];
      getActiveProjects().then(function(list){managePhasesProjects=list||[];if(managePhasesOpen)render();}).catch(function(){managePhasesProjects=[];});
    }
    render();
    if(managePhasesOpen){var pn=gid('mgphase-panel');if(pn&&pn.scrollIntoView)pn.scrollIntoView({block:'start',behavior:'smooth'});var mi=gid('mgphase-input');if(mi)mi.focus({preventScroll:true});}
  }
  function onManagePhasesEvent(){ toggleManagePhases(); }
  window.addEventListener('timecard-manage-phases', onManagePhasesEvent);
  // A phase was opened or closed for time entry (services/phases.js): refresh the phase lists.
  function onPhaseTimeChanged(){ tc.getEntryPhases().then(function(list){ allPhases = list || []; render(); }).catch(function(){}); }
  window.addEventListener('phase-time-entry-changed', onPhaseTimeChanged);

  // ── Document-level listeners (tracked for cleanup) ──
  function onDocClickCloseSD(e){ if(activeSD&&!e.target.closest('.sd-wrap')) closeSD(); }
  function onWindowFocus(){ if(canReload()) refreshRecords(); }
  // An accepted suggestion created an entry outside this tool — reload so it shows.
  function onExternalRefresh(){ fetchInFlight = false; refreshRecords(); }
  document.addEventListener('click', onDocClickCloseSD);
  document.addEventListener('click', handleAction, true);
  window.addEventListener('focus', onWindowFocus);
  window.addEventListener('timecard-refresh', onExternalRefresh);
  var pollId = setInterval(function(){ if(canReload()) refreshRecords(); }, 120000);

  // ── Boot ──
  container.innerHTML = '<div class="fetch-bar"><span class="fetch-spinner"></span> Loading…</div>';
  fetchActivities();
  fetchSettings();
  fetchPhases();
  fetchMyWorkItems();
  refreshLocked();
  if (getSubmitterId()) getCanSelfApprove(getSubmitterId()).then(function(v){ canSelfApprove = !!v; if (initialized) render(); }).catch(function(){});
  fetchRecords(function(records){
    allRecords=records; initialized=true;
    // Deep-link: if opened to a specific day (from the enforcement gate), jump to that day + week.
    var initDs = (initialDate && /^\d{4}-\d{2}-\d{2}$/.test(initialDate)) ? initialDate : null;
    selectedDate = initDs || formatDate(new Date());
    if (initDs) {
      var clicked = new Date(initDs+'T00:00:00'), thisM = getMonday(new Date());
      weekOffset = Math.round((getMonday(clicked)-thisM)/(7*24*3600*1000));
    }
    render();
  });

  // Cleanup
  return function cleanup(){
    document.removeEventListener('click', onDocClickCloseSD);
    window.removeEventListener('timecard-manage-phases', onManagePhasesEvent);
    window.removeEventListener('phase-time-entry-changed', onPhaseTimeChanged);
    document.removeEventListener('click', handleAction, true);
    window.removeEventListener('focus', onWindowFocus);
    window.removeEventListener('timecard-refresh', onExternalRefresh);
    clearInterval(pollId);
  };
}
